import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next';
import { md5 } from 'js-md5';

/* Snackbar
 * https://notistack.com/api-reference
 */
import { SnackbarProvider, useSnackbar } from 'notistack';

// Components
import Max2Menu from './components/menu/menu';
import Max2Dashboard from './components/dashboard/dashboard';
import Max2Settings from './components/settings/settings';
import Max2PageObject from './components/object/page';
import Max2PageMedia from './components/media/page';
import Max2PageContract from './components/contract/page';
import Max2ModalSpecial from './components/contract/specials_modal';
import AjaxController from './components/system/ajaxController';
import Max2ModalAbout from './components/system/about';
import Max2ModalProfile from './components/system/profile';
import Max2ModalWatchlist from './components/system/watchlist';

// config
import { systemdata_definitions } from './components/settings/definitiions';

// Redux slices
import { setActivePage, setUser, setToken, setSystemData } from './appSlice'

// SemanticUI components
import { Container, Header, HeaderContent, Image, Button, Segment, Form, Grid, Dropdown, DropdownMenu, DropdownItem } from 'semantic-ui-react';

// Styles
import 'semantic-ui-css/semantic.min.css'
import './App.css';

// Other Imports
import EWHLogo from './resources/images/ewh_logo.svg'



/**
 * 
 * @param {Function} onLoginCallback 
 * @returns 
 */
const Max2Login = ({onLoginCallback}) => {
  // local state
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');

  // global state
  const dispatch = useDispatch();
  const version     = useSelector((state) => state.app.version);
  const backendUrl  = useSelector((state) => state.app.backend.baseProtocol) + '://' + useSelector((state) => state.app.backend.url);

  // Snackbar
  const { enqueueSnackbar } = useSnackbar();

  // Translation
  const { t, i18n } = useTranslation();
  const changeLanguage = (lng) => i18n.changeLanguage(lng);

  let intervalId = null;

  

  /**
   * @async
   *
  const checkLoginStatus = () => {
    fetch(backendUrl + '/user/check_auth', { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        if (data.loggedIn) {
          console.log('Eingeloggt als:', data.user.username);
        } else {
          console.log('Nicht eingeloggt');
          enqueueSnackbar(t('login.error.notLoggedIn'), { variant: 'error' });
          dispatch(setActivePage('START'));
          dispatch(setUser(null));
          dispatch(setToken(null));
        }
      });
  }
  /**/



  /**
   * @async
   */
  const isLoggedIn = () => {
    console.log('isLoggedIn()');
    fetch(backendUrl + '/user/check_auth', { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        if (!data.loggedIn) {
          console.log('Nicht eingeloggt');
          enqueueSnackbar(t('login.error.notLoggedIn'), { variant: 'error' });
          dispatch(setActivePage('START'));
          dispatch(setUser(null));
          dispatch(setToken(null));
          clearInterval(intervalId);
        }
      });
  }



  /**
   * @async
   */
  const handleLogin = async () => {
    if (!login) {
      enqueueSnackbar(t('login.error.missingEmail'), { variant: 'error' });
      return false;
    }

    if (!password) {
      enqueueSnackbar(t('login.error.missingPassword'), { variant: 'error' });
      return false;
    }
    
    const data  = { login: md5(login), password: md5(password) }
    const api   = new AjaxController(backendUrl);
    try {
      const response = await api.post('/user/login', data);
      if (response?.user) {
        dispatch(setUser(response?.user));
      }
      if (response?.meta?.token) {
        dispatch(setToken(response?.user));
        intervalId = setInterval(isLoggedIn, 15000);
        onLoginCallback();
      }
    } catch (error) {
      enqueueSnackbar(t('login.error.failed'), { variant: 'error' });
      dispatch(setActivePage('START'));
      dispatch(setUser(null));
      dispatch(setToken(null));
      clearInterval(intervalId);
    }
  };

  return (
    <Grid textAlign="center" verticalAlign="middle">
      <Grid.Column style={{ maxWidth: 450 }}>
        <Form size="large" onSubmit={handleLogin}>
          <Segment raised style={{background: 'rgb(0 52 115 / 0.5)', borderRadius: '15px', padding: '25px'}}>
            <Header as="h2" textAlign="center" style={{color: '#003473'}}>
              {t('login.title')}
            </Header>
            <Dropdown item icon='world' style={{marginBottom: '20px'}}>
              <DropdownMenu>
                <DropdownItem onClick={() => changeLanguage('de')}>DE</DropdownItem>
                <DropdownItem onClick={() => changeLanguage('en')}>EN</DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <Form.Input
              fluid
              icon="user"
              iconPosition="left"
              placeholder={t('login.username')}
              value={login}
              onChange={(e) => setLogin(e.target.value)}
            />
            <Form.Input
              fluid
              icon="lock"
              iconPosition="left"
              placeholder={t('login.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button circular primary fluid size="large" type="submit" style={{marginTop: '30px'}}>
              {t('login.button')}
            </Button>
          </Segment>
        </Form>
        <Container style={{marginTop: '50px'}}>
          {t('footer.version')}: {version}
          <br /><br />
          {t('footer.copyright')}
        </Container>
      </Grid.Column>
    </Grid>
  );
};



/**
 * 
 * @returns 
 */
const Max2Startup = () => {
  // global state
  const dispatch = useDispatch();
  const backendUrl  = useSelector((state) => state.app.backend.baseProtocol) + '://' + useSelector((state) => state.app.backend.url);

  // Snackbar
  const { enqueueSnackbar } = useSnackbar();

  // Translation
  const { t } = useTranslation();


  /**
   * @async
   */
  const loadSystemData = async () => {
    const api   = new AjaxController(backendUrl);
    try {
      let params = []
      for (const table in systemdata_definitions) {
        if (!Object.hasOwn(systemdata_definitions, table)) continue;
        const definition = systemdata_definitions[table];
        params.push({
          table: definition.table,
          sorting: {
            by: definition.defaultSorting,
            direction: 'ASC'
          }
        });
      }
      const response = await api.get('/settings/systemdata/' + btoa(JSON.stringify(params)).replaceAll('=', '_'));
      if (response?.records) {
        console.log('Laden der Systemdaten erfolgreich!');
        dispatch(setSystemData(response.records));
      }
    } catch (error) {
      enqueueSnackbar(t('login.error.failedSystemdata'), { variant: 'error' });
      dispatch(setActivePage('START'));
      dispatch(setUser(null));
      dispatch(setToken(null));
    }
  };


  const loginCallback = () => {
    console.log('Login erfolgreich! Lade Systemdaten...');
    loadSystemData();

    console.log('Lade Dashboard...');
    dispatch(setActivePage('DASHBOARD'));
  }
  
  return (
    <Segment style={{height: '100vh', background: 'transparent'}}>
        <Container textAlign='center' style={{padding: '50px', background: 'white', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', borderRadius: '25px'}}>
          {/*<Image size='large' src={BinoliLogo} alt="Binoli Logo" centered />*/}
          <Image size='large' src={EWHLogo} alt="EWH Logo" centered />
          <Header as='h1' icon textAlign='center' style={{marginBottom: '50px'}}>
            <HeaderContent>MAX<sup>2</sup></HeaderContent>
          </Header>
          <Max2Login onLoginCallback={() => loginCallback()} />
        </Container>
    </Segment>
  );
}



/**
 * 
 * @returns 
 */
function App() {
  // global state
  const dispatch = useDispatch();
  const aktivePage    = useSelector((state) => state.app.activePage)
  const isDirty       = useSelector((state) => state.object.isDirty)
  const backendUrl    = useSelector((state) => state.app.backend.baseProtocol) + '://' + useSelector((state) => state.app.backend.url);

  // Snackbar
  const { enqueueSnackbar } = useSnackbar();

  // Translation
  const { t } = useTranslation();

  /**
   * Als useCallback, stabilisiert für Hook-Abhängigkeiten
   * @async
   */
  const loadSystemData = useCallback(async () => {
    const api = new AjaxController(backendUrl);
    try {
      let params = []
      for (const table in systemdata_definitions) {
        if (!Object.hasOwn(systemdata_definitions, table)) continue;
        const definition = systemdata_definitions[table];
        params.push({
          table: definition.table,
          sorting: {
            by: definition.defaultSorting,
            direction: 'ASC'
          }
        });
      }
      const response = await api.get('/settings/systemdata/' + btoa(JSON.stringify(params)).replaceAll('=', '_'));
      if (response?.records) {
        console.log('Laden der Systemdaten erfolgreich!');
        dispatch(setSystemData(response.records));
      }
    } catch (error) {
      enqueueSnackbar(t('login.error.failedSystemdata'), { variant: 'error' });
      dispatch(setActivePage('START'));
      dispatch(setUser(null));
      dispatch(setToken(null));
    }
  }, [backendUrl, dispatch, enqueueSnackbar, t]);



  /**
   * @async
   */
  const checkLoginStatus = useCallback(() => {
    fetch(backendUrl + '/user/check_auth', { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        if (data.loggedIn) {

          console.log('Lade Systemdaten...');
          loadSystemData();

          if (aktivePage === 'START') {
            dispatch(setActivePage('DASHBOARD'));
            dispatch(setUser(data.user));
          }
        } else {
          console.log('Nicht eingeloggt');
          dispatch(setActivePage('START'));
        }
      });
  }, [backendUrl, aktivePage, dispatch, loadSystemData]);

  // check if user is logged in
  useEffect(() => {
    console.log('App() -> useEffect()');
    checkLoginStatus();
  }, [checkLoginStatus]);

  
  let menu = aktivePage === 'START' ? <></> : <Max2Menu />;
  let page = <></>;
  let style = {};

  console.log('[App() -> aktivePage]', aktivePage)
  
  switch (aktivePage) {
    case 'DASHBOARD': 
      style = {height: 'calc(100vh - 58px)', overflow: 'auto'};
      page = <>{menu}<Container fluid className='App-content' style={style}><Max2Dashboard /></Container></>; 
      break;
    
    case 'SETTINGS': 
      style = {height: 'calc(100vh - 58px)', overflow: 'auto'};
      page = <>{menu}<Container fluid className='App-content' style={style}><Max2Settings /></Container></>; 
      break;
    
    case 'OBJECT': 
      style = isDirty ? {height: 'calc(100vh - 116px)', overflow: 'auto'} : {height: 'calc(100vh - 61px)', overflow: 'auto'};
      page = <>{menu}<Container fluid className='App-content' style={style}><Max2PageObject /></Container></>; 
      break;
  
    case 'CONTRACT': 
      style = isDirty ? {height: 'calc(100vh - 116px)', overflow: 'auto'} : {height: 'calc(100vh - 61px)', overflow: 'auto'};
      page = <>{menu}<Container fluid className='App-content' style={style}><Max2PageContract /></Container></>; 
      break;

    case 'MEDIA': 
      style = isDirty ? {height: 'calc(100vh - 116px)', overflow: 'auto'} : {height: 'calc(100vh - 61px)', overflow: 'auto'};
      page = <>{menu}<Container fluid className='App-content' style={style}><Max2PageMedia /></Container></>; 
      break;

    default: 
      page = <Max2Startup />;
  }

  return (
    <div className="App" style={{background: '#701F4B'}}>
      {page}
      <Max2ModalSpecial />
      <Max2ModalAbout />
      <Max2ModalProfile />
      <Max2ModalWatchlist />
    </div>
  );
}

//export default App;

export default function IntegrationNotistack() {
  return (
    <SnackbarProvider maxSnack={5} anchorOrigin={{horizontal: 'center', vertical: 'bottom'}}>
      <App />
    </SnackbarProvider>
  );
}